export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  premium: string;
  onboard: string;
  createdAt: string;
  capital?: number;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  needsOnboarding?: boolean;
  onboardingStep?: 'language' | 'welcome' | 'capital' | 'complete';
}

export interface RegisterData {
  email: string;
  password: string;
}

export interface UserAsset {
  symbol: string;
  amount: number;
  purchasePrice: number;
}

class AuthService {
  private static readonly LOGIN_URL = 'https://login-api.login-api.workers.dev/v1/login';
  private static readonly API_URL = 'https://api.cryptonvx.com/v1';
  private static readonly GOOGLE_LOGIN_URL = process.env.REACT_APP_GOOGLE_LOGIN_URL || `${'https://api.cryptonvx.com/v1'}/auth/google`;
  private token: string | null = null;
  private user: User | null = null;

  constructor() {
    // Récupérer le token du localStorage au démarrage (même format que web-platform)
    const tokenData = localStorage.getItem('token');
    if (tokenData) {
      try {
        const parsedData = JSON.parse(tokenData);
        this.token = parsedData.token;
      } catch (error) {
        console.error('Erreur lors du parsing du token:', error);
        localStorage.removeItem('token');
      }
    }
  }

  async googleLogin(idToken: string): Promise<LoginResponse> {
    try {
      if (!idToken) {
        throw new Error('Jeton Google invalide.');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 50000);

      const response = await fetch(AuthService.GOOGLE_LOGIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let message = 'Erreur de connexion Google.';
        try {
          const err = await response.json();
          if (err && (err.error || err.message)) {
            message = err.error || err.message;
          }
        } catch {}
        throw new Error(message);
      }

      // Le backend peut renvoyer une chaîne brute (token) ou un JSON { token }
      let token: string | undefined;
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          token = typeof parsed === 'string' ? parsed : parsed.token;
        } catch {
          token = text.trim();
        }
      }

      if (!token) {
        throw new Error('Token non reçu du serveur Google.');
      }

      // Stocker comme pour login classique
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token invalide reçu du serveur');
      }

      const payload = JSON.parse(atob(parts[1]));

      const tokenData = JSON.stringify({ remember: false, token });
      localStorage.setItem('token', tokenData);
      this.token = token;

      // Récupérer les informations utilisateur
      const userInfo = await this.getUserInfo();
      if (userInfo) {
        this.user = {
          id: payload.id || userInfo.id || '',
          firstName: userInfo.firstname || '',
          lastName: userInfo.lastname || '',
          email: userInfo.email || payload.username || payload.email || '',
          premium: userInfo.premium || 'no',
          onboard: userInfo.onboard || 'no',
          createdAt: new Date().toISOString(),
          capital: userInfo.capital || 0,
        };

        // Vérifier onboarding
        let needsOnboarding = this.user.onboard !== 'yes';
        let onboardingStep: 'language' | 'welcome' | 'capital' | 'complete' | undefined = undefined;
        if (this.user.onboard !== 'yes') {
          switch (this.user.onboard) {
            case '0': onboardingStep = 'language'; break;
            case '1': onboardingStep = 'welcome'; break;
            case '2': onboardingStep = 'capital'; break;
            case '3': onboardingStep = 'complete'; break;
            default: onboardingStep = 'language';
          }
        }

        return { success: true, token, user: this.user, needsOnboarding, onboardingStep };
      }

      return { success: true, token, user: this.user || undefined, needsOnboarding: false };
    } catch (error: any) {
      console.error('Erreur de connexion Google:', error);
      throw new Error(error.message || 'Erreur lors de la connexion avec Google');
    }
  }

  async googleLoginWithCode(code: string): Promise<LoginResponse> {
    try {
      if (!code) {
        throw new Error('Code Google invalide.');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 50000);

      const response = await fetch(AuthService.GOOGLE_LOGIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let message = 'Erreur de connexion Google.';
        try {
          const err = await response.json();
          if (err && (err.error || err.message)) {
            message = err.error || err.message;
          }
        } catch {}
        throw new Error(message);
      }

      // Le backend peut renvoyer une chaîne brute (token) ou un JSON { token }
      let token: string | undefined;
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          token = typeof parsed === 'string' ? parsed : parsed.token;
        } catch {
          token = text.trim();
        }
      }

      if (!token) {
        throw new Error('Token non reçu du serveur Google.');
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token invalide reçu du serveur');
      }
      const payload = JSON.parse(atob(parts[1]));

      const tokenData = JSON.stringify({ remember: false, token });
      localStorage.setItem('token', tokenData);
      this.token = token;

      const userInfo = await this.getUserInfo();
      if (userInfo) {
        this.user = {
          id: payload.id || userInfo.id || '',
          firstName: userInfo.firstname || '',
          lastName: userInfo.lastname || '',
          email: userInfo.email || payload.username || payload.email || '',
          premium: userInfo.premium || 'no',
          onboard: userInfo.onboard || 'no',
          createdAt: new Date().toISOString(),
          capital: userInfo.capital || 0,
        };

        let needsOnboarding = this.user.onboard !== 'yes';
        let onboardingStep: 'language' | 'welcome' | 'capital' | 'complete' | undefined = undefined;
        if (this.user.onboard !== 'yes') {
          switch (this.user.onboard) {
            case '0': onboardingStep = 'language'; break;
            case '1': onboardingStep = 'welcome'; break;
            case '2': onboardingStep = 'capital'; break;
            case '3': onboardingStep = 'complete'; break;
            default: onboardingStep = 'language';
          }
        }

        return { success: true, token, user: this.user, needsOnboarding, onboardingStep };
      }

      return { success: true, token, user: this.user || undefined, needsOnboarding: false };
    } catch (error: any) {
      console.error('Erreur de connexion Google (code flow):', error);
      throw new Error(error.message || 'Erreur lors de la connexion avec Google');
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      // Validation des entrées
      if (!email.trim()) {
        throw new Error("L'email est requis.");
      }
      if (!password.trim()) {
        throw new Error("Le mot de passe est requis.");
      }

      console.log('Tentative de connexion pour:', email);

      // Utiliser la même API que web-platform
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 50000);

      const response = await fetch(
        `${AuthService.LOGIN_URL}?u=${encodeURIComponent(email)}&p=${encodeURIComponent(password)}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);

      if (response.status === 200) {
        const responseBody = await response.text();
        
        // Vérifier si c'est une erreur
        if (responseBody.includes('error')) {
          const errorData = JSON.parse(responseBody);
          throw new Error(errorData.error);
        }

        // Décoder le JWT comme dans web-platform
        let token: string;
        try {
          token = JSON.parse(responseBody) as string;
        } catch (e) {
          token = responseBody.trim();
        }

        // Vérifier le format JWT
        const parts = token.split('.');
        if (parts.length !== 3) {
          throw new Error('Token invalide reçu du serveur');
        }

        // Décoder le payload pour vérifier l'utilisateur
        const payload = JSON.parse(atob(parts[1]));
        
        if (payload.username !== email) {
          throw new Error("Nom d'utilisateur incorrect.");
        }

        // Stocker le token comme dans web-platform
        const tokenData = JSON.stringify({ remember: false, token });
        localStorage.setItem('token', tokenData);
        
        this.token = token;

        // Récupérer les informations utilisateur
        const userInfo = await this.getUserInfo();
        if (userInfo) {
          this.user = {
            id: payload.id,
            firstName: userInfo.firstname || '',
            lastName: userInfo.lastname || '',
            email: payload.username,
            premium: userInfo.premium || 'no',
            onboard: userInfo.onboard,
            createdAt: new Date().toISOString(),
            capital: userInfo.capital || 0,
          };

          // Vérifier le statut d'onboarding
          let needsOnboarding = this.user.onboard != '3';
          let onboardingStep: 'language' | 'welcome' | 'capital' | 'complete' | undefined = undefined;

          if (this.user.onboard == '3') {
            needsOnboarding = false; // L'onboarding est terminé
            onboardingStep = undefined; // Pas d'étape spécifique requise pour l'onboarding
          } else {
            needsOnboarding = true; // L'onboarding n'est pas terminé
            if (this.user.onboard == '0') {
              onboardingStep = 'language';
            } else if (this.user.onboard == '1') {
              onboardingStep = 'welcome';
            } else if (this.user.onboard == '2') {
              onboardingStep = 'capital';
            } else {
              onboardingStep = 'complete';
            }
          }

          return {
            success: true,
            token,
            user: this.user,
            needsOnboarding,
            onboardingStep
          };
        }

        return {
          success: true,
          token,
          user: this.user || undefined,
          needsOnboarding: false
        };
      } else {
        throw new Error('Erreur de connexion. Veuillez réessayer.');
      }
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      throw new Error(error.message || 'Erreur de connexion au serveur');
    }
  }

  async register(userData: RegisterData): Promise<LoginResponse> {
    try {
      // Validation des entrées
      if (!userData.email.trim()) {
        throw new Error("L'email est requis.");
      }
      if (!userData.password.trim()) {
        throw new Error("Le mot de passe est requis.");
      }
      if (userData.password.length < 6) {
        throw new Error("Le mot de passe doit contenir au moins 6 caractères.");
      }

      const dataPOST = JSON.stringify({
        u: userData.email,
        p: userData.password,
      });

      const response = await fetch(AuthService.LOGIN_URL, {
        method: 'POST',
        body: dataPOST,
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        credentials: 'include'
      });

      // En mode 'no-cors', nous ne pouvons pas lire le corps de la réponse de manière fiable.
      // Nous assumons que la requête a été envoyée avec succès si fetch ne lève pas d'erreur.
      // Le succès réel de l'inscription sera confirmé par la tentative de connexion automatique.

      return { success: true, message: 'Inscription traitée.' }; // Indiquer le succès de l'opération
    } catch (error: any) {
      throw new Error(error.message || 'Erreur lors de l\'inscription ou de la connexion au serveur');
    }
  }

  private getEmailFromToken(): string | null {
    if (!this.token) return null;
    
    try {
      const parts = this.token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload.username || null;
    } catch (error) {
      console.error('Erreur lors du décodage du JWT:', error);
      return null;
    }
  }

  async getUserInfo(): Promise<any> {
    try {
      if (!this.token) {
        throw new Error('Token non disponible');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 50000);

      const response = await fetch(
        `${AuthService.API_URL}/app?jwt=${encodeURIComponent(this.token)}&type=informations`,
        {
          signal: controller.signal,
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        }
      );
      clearTimeout(timeoutId);

      if (response.status === 200) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const userInfo = data[0];
          // Ajouter l'email depuis le JWT
          userInfo.email = this.getEmailFromToken();
          // Mettre à jour l'utilisateur dans le service
          this.updateUserFromInfo(userInfo);
          return userInfo;
        }
      }
      
      throw new Error('Impossible de récupérer les informations utilisateur');
    } catch (error) {
      console.error('Erreur getUserInfo:', error);
      return null;
    }
  }

  private updateUserFromInfo(userInfo: any): void {
    if (userInfo) {
      this.user = {
        id: userInfo.id || '',
        firstName: userInfo.firstname || '',
        lastName: userInfo.lastname || '',
        email: userInfo.email || '',
        premium: userInfo.premium || 'no',
        onboard: userInfo.onboard || 'no',
        createdAt: userInfo.createdAt || new Date().toISOString(),
        capital: userInfo.capital || 0,
      };
    }
  }

  logout(): void {
    this.token = null;
    this.user = null;
    localStorage.removeItem('token');
  }

  getToken(): string | null {
    if (!this.token) {
      // Essayer de récupérer depuis localStorage
      const tokenData = localStorage.getItem('token');
      if (tokenData) {
        try {
          const parsedData = JSON.parse(tokenData);
          this.token = parsedData.token;
        } catch (error) {
          console.error('Erreur lors du parsing du token:', error);
          localStorage.removeItem('token');
        }
      }
    }
    return this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Méthode pour obtenir les headers d'authentification
  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  // Méthode pour vérifier si le token est valide
  async verifyToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Vérifier avec l'API des informations utilisateur
      const userInfo = await this.getUserInfo();
      return !!userInfo;
    } catch (error) {
      // Token invalide, nettoyer les données
      this.logout();
      return false;
    }
  }

  // Méthode pour mettre à jour le profil utilisateur
  async updateProfile(userData: User): Promise<User> {
    try {
      const response = await fetch(`${AuthService.API_URL}/app/jwt/informations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify({
          firstname: userData.firstName,
          lastname: userData.lastName,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour du profil');
      }

      const updatedUser = await response.json();
      this.updateUserFromInfo(updatedUser);
      return this.getUser()!;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }

  // Méthode pour changer le mot de passe
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!this.token) {
      throw new Error('Non authentifié');
    }

    try {
      // Simulation pour l'instant
      if (currentPassword.length < 6) {
        throw new Error('Mot de passe actuel incorrect');
      }
      return;
    } catch (error: any) {
      throw new Error(error.message || 'Erreur de connexion au serveur');
    }
  }

  // Méthode pour demander la réinitialisation de mot de passe
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!email.trim()) {
        throw new Error("L'email est requis.");
      }

      const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        throw new Error("Veuillez saisir une adresse email valide.");
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Utiliser l'endpoint GET existant ?reset=x
      const response = await fetch(`${AuthService.LOGIN_URL}?reset=${encodeURIComponent(email.toLowerCase())}`, {
        method: 'GET',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let message = 'Erreur lors de la demande de réinitialisation.';
        try {
          const err = await response.json();
          if (err && (err.error || err.message)) {
            message = err.error || err.message;
          }
        } catch {}
        throw new Error(message);
      }

      const data = await response.json();
      return { success: true, message: data.message || 'Email de réinitialisation envoyé avec succès.' };
    } catch (error: any) {
      console.error('Erreur demande réinitialisation:', error);
      throw new Error(error.message || 'Erreur lors de la demande de réinitialisation');
    }
  }

  // Anciennes méthodes liées à la réinitialisation via API supprimées au profit du flux 100% client

  // Ancien saveUserOnboardingData - mis en commentaire car le flux de la web-platform est différent
  /*
  async saveUserOnboardingData(capital: number, currency: string): Promise<User | null> {
    try {
      if (!this.token || !this.user) {
        throw new Error('Non authentifié ou informations utilisateur manquantes.');
      }

      const response = await fetch(`${AuthService.API_URL}/app?jwt=${encodeURIComponent(this.token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        credentials: 'include',
        body: JSON.stringify({
          type: 'onboard',
          capital: capital,
          currency: currency,
          onboard: 'yes',
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la sauvegarde des données d\'onboarding.');
      }

      const data = await response.json();
      if (data && data.error) {
        throw new Error(data.error);
      }

      // Update the user object in the service
      if (this.user) {
        this.user = {
          ...this.user,
          onboard: 'yes',
        };
      }
      return this.user;

    } catch (error: any) {
      console.error('Erreur saveUserOnboardingData:', error);
      throw new Error(error.message || 'Erreur lors de la sauvegarde des données d\'onboarding.');
    }
  }
  */

  async saveUserAssets(assets: UserAsset[]): Promise<boolean> {
    try {
      if (!this.token || !this.user) {
        throw new Error('Non authentifié ou informations utilisateur manquantes.');
      }

      if (assets.length === 0) {
        return true;
      }

      const pairs = assets.map(asset => asset.symbol);
      const quantity = assets.map(asset => asset.amount);
      const bid_price = assets.map(asset => asset.purchasePrice);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 50000);

      try {
        const response = await fetch(
          `${AuthService.API_URL}/app?jwt=${encodeURIComponent(this.token)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            credentials: 'include',
            body: JSON.stringify({
              pairs,
              quantity,
              bid_price
            }),
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        // ⚠️ en mode no-cors, on ne pourra pas lire le body/résultat
        // return response; 

      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          console.error('Erreur lors de la sauvegarde des actifs de l\'utilisateur.');
        } else {
          console.error('Erreur lors de la sauvegarde des actifs de l\'utilisateur. :', error);
        }
        throw error;
      }

      return true;

    } catch (error: any) {
      console.error('Erreur saveUserAssets:', error);
      throw new Error(error.message || 'Erreur lors de la sauvegarde des actifs de l\'utilisateur.');
    }
  }

  async updateUserInitialInfo(firstName: string, lastName: string, age: number): Promise<User | null> {
    try {
      if (!this.token) {
        throw new Error('Non authentifié.');
      }

      const params = new URLSearchParams({
        jwt: this.token,
        onboard: '1',
        firstname: encodeURIComponent(firstName),
        lastname: encodeURIComponent(lastName),
        age: age.toString()
      });

      const response = await fetch(`${AuthService.API_URL}/app?${params}`, {
        method: 'GET',
        // Pour les requêtes GET, 'no-cors' et 'credentials: 'include' ne sont généralement pas nécessaires ou ont un comportement différent.
        // Je les omets ici pour les requêtes GET, en me basant sur le comportement de l'ancienne plateforme.
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour des informations initiales d\'onboarding.');
      }

      const data = await response.json();
      if (data && data.error) {
        throw new Error(data.error);
      }

      // Mettre à jour l'objet utilisateur avec les nouvelles informations
      // et marquer l'étape 'onboard' si nécessaire (si l'API renvoie cette info)
      const userInfo = await this.getUserInfo(); // Re-fetch user info to get updated 'onboard' status
      return userInfo;

    } catch (error: any) {
      console.error('Erreur updateUserInitialInfo:', error);
      throw new Error(error.message || 'Erreur lors de la mise à jour des informations d\'onboarding.');
    }
  }

  async completeOnboarding(): Promise<boolean> {
    try {
      if (!this.token) {
        throw new Error('Non authentifié.');
      }

      const response = await fetch(
        `${AuthService.API_URL}/app?jwt=${encodeURIComponent(this.token)}&onboardSKIP=yes`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la finalisation de l\'onboarding.');
      }

      const data = await response.json();
      if (data && data.error) {
        throw new Error(data.error);
      }

      // Mettre à jour le statut 'onboard' de l'utilisateur à 'yes'
      if (this.user) {
        this.user = { ...this.user, onboard: 'yes' };
      }

      return true;

    } catch (error: any) {
      console.error('Erreur completeOnboarding:', error);
      throw new Error(error.message || 'Erreur lors de la finalisation de l\'onboarding.');
    }
  }
}

export const authService = new AuthService();